export interface Question {
  id: number;
  text: string;
  partTitle: string;
  type?: 'choice' | 'slider';
  options: {
    label: string;
    value: 'A' | 'B' | 'C' | 'D' | 'E';
    text: string;
    description?: string;
  }[];
}

export interface ChapterFeedback {
  id: number;
  title: string;
  subtitle: string;
  feedbacks: Record<'A' | 'B' | 'C' | 'D', {
    roast: string;
    transition: string;
    label: string;
  }>;
}

export interface InvestorProfile {
  id: string;
  name: string;
  traits: string[];
  description: string;
  detailedAnalysis: {
    title: string;
    content: string;
  }[];
  investmentStyle: string;
  riskTolerance: string;
  timeHorizon: string;
  portfolioAnalysis: string;
  allocation: {
    name: string;
    value: number;
    explanation: string;
    examples: string[];
  }[];
  sectors: string[];
  topFunds: {
    name: string;
    manager?: string;
    thesis: string;
    matchReason: string;
    returns?: string;
    risk: string;
  }[];
  sampleHoldings: string[];
  radarData: {
    subject: string;
    A: number;
    fullMark: number;
  }[];
}

export const QUIZ_CHAPTERS: ChapterFeedback[] = [
  {
    id: 1,
    title: "Part 1: Decision-Making",
    subtitle: "Intuition vs. Data",
    feedbacks: {
      A: { label: "The Intuitive", roast: "So you\'re the \'trust your gut\' type? That\'s giving main character energy, but let\'s see if your gut can handle a 20% market dip.", transition: "Can you handle real risk?" },
      B: { label: "The Analyst", roast: "Analysis paralysis much? You\'re giving \'I read the 200-page prospectus for fun.\' Numbers are cool, but they don\'t always tell the future.", transition: "Will your math hold up under pressure?" },
      C: { label: "The Collaborator", roast: "Collective wisdom is nice, but don\'t get lost in the sauce. Shared wisdom isn\'t always right—just ask everyone who bought the top.", transition: "What is your appetite for risk?" },
      D: { label: "The Observer", roast: "Always watching the pros, huh? Influence is one thing, but your wallet is your own. Let\`s see if you can lead instead of follow.", transition: "Ready for high-stakes scenarios?" }
    }
  },
  {
    id: 2,
    title: "Part 2: Risk & Opportunity",
    subtitle: "Moonshots vs. Steady Growth",
    feedbacks: {
      A: { label: "The Daredevil", roast: "Absolute chaos energy. You see a 🚀 and you\'re in. We love the hustle, but hopefully you have a backup plan for when things go south.", transition: "Where do you get your alpha?" },
      B: { label: "The Preserver", roast: "Playing it safe? Your portfolio is giving \'early retirement at 85.\' Steady wins the race, but a little spice never hurt anyone.", transition: "How do you find \'safe\' bets?" },
      C: { label: "The Consensus Builder", roast: "Steady returns with the squad? That\'s wholesome. But a group can be wrong together. Let\'s see if you\'re actually doing the homework.", transition: "Where do you get your investment ideas?" },
      D: { label: "The Benchmark", roast: "Market average? You\'re basically a walking index fund. It\'s efficient, but where\'s the edge? Let\'s talk research.", transition: "Does your research go deep enough?" }
    }
  },
  {
    id: 3,
    title: "Part 3: Info & Research",
    subtitle: "Where You Get Your Alpha",
    feedbacks: {
      A: { label: "The Futurist", roast: "Futurist vibes. You\'re living in 2035 while we\'re all in 2026. Just make sure the tech actually works before you go all in.", transition: "How will you manage this vision?" },
      B: { label: "The Accountant", roast: "Balance sheets as bedtime stories? Respect the grind, but sometimes the vibes matter as much as the P/E ratio.", transition: "What is your management style?" },
      C: { label: "The Networker", roast: "Community picks are great until the community disappears. Don\'t be the last one holding the bag. Let\'s see your management plan.", transition: "How often do you check the charts?" },
      D: { label: "The Technician", roast: "Chart patterns and real-time data? You\'re basically a human Bloomberg terminal. Just don\'t let the noise drown out the signal.", transition: "Are you a holder or a trader?" }
    }
  },
  {
    id: 4,
    title: "Part 4: Management",
    subtitle: "Active vs. Passive",
    feedbacks: {
      A: { label: "The Active Trader", roast: "Always thinking about the next play. You\'re definitely the friend who checks their portfolio at dinner. Obsessive, but committed.", transition: "What is the soul of your strategy?" },
      B: { label: "The Planner", roast: "Scheduled reviews? So organized. Your calendar is probably beautiful, but the market doesn\'t always wait for your 2 PM appointment.", transition: "What is your investment philosophy?" },
      C: { label: "The Social Investor", roast: "Waiting for the group chat to ping? Teamwork makes the dream work, but sometimes you gotta pull the trigger yourself.", transition: "What is your ultimate goal?" },
      D: { label: "The Strategist", roast: "Adaptive and data-driven. You\'re like a heat-seeking missile for gains. Just don\'t over-trade yourself into a tax nightmare.", transition: "What do you actually care about?" }
    }
  }
];

export const QUIZ_QUESTIONS: Question[] = [
  {
    id: 1,
    partTitle: "Part 1: Decision-Making",
    text: "When making important financial decisions, you prefer to:",
    options: [
      { label: 'A', value: 'A', text: "Trust your gut feeling and intuition" },
      { label: 'B', value: 'B', text: "Analyze all available data and research thoroughly" },
      { label: 'C', value: 'C', text: "Consult with experts and trusted advisors" },
      { label: 'D', value: 'D', text: "Look at what successful investors are doing" },
    ],
  },
  {
    id: 2,
    partTitle: "Part 1: Decision-Making",
    text: "You\'re researching a new investment opportunity. What catches your attention first?",
    options: [
      { label: 'A', value: 'A', text: "The company\'s innovative vision and future potential" },
      { label: 'B', value: 'B', text: "Solid financials, track record, and proven results" },
      { label: 'C', value: 'C', text: "The team behind it and their expertise" },
      { label: 'D', value: 'D', text: "Market trends and momentum" },
    ],
  },
  {
    id: 3,
    partTitle: "Part 1: Decision-Making",
    text: "How do you feel about companies that are \"ahead of their time\"?",
    options: [
      { label: 'A', value: 'A', text: "Exciting! I love investing in the future" },
      { label: 'B', value: 'B', text: "Cautious - I prefer proven business models" },
      { label: 'C', value: 'C', text: "Interested if they have the right leadership" },
      { label: 'D', value: 'D', text: "Depends on market validation and adoption rates" },
    ],
  },
  {
    id: 4,
    partTitle: "Part 2: Risk & Opportunity",
    text: "A hot new tech startup is getting buzz. Your reaction?",
    options: [
      { label: 'A', value: 'A', text: "🚀 I want to get in early before everyone else!" },
      { label: 'B', value: 'B', text: "🤔 I\'ll wait to see their next earnings report" },
      { label: 'C', value: 'C', text: "🔍 Let me research the founders and their track record" },
      { label: 'D', value: 'D', text: "📊 Show me the user growth metrics and market size" },
    ],
  },
  {
    id: 5,
    partTitle: "Part 2: Risk & Opportunity",
    text: "Your portfolio dropped 15% in a month. What\'s your emotional response?",
    options: [
      { label: 'A', value: 'A', text: "This is temporary - innovation takes time to pay off" },
      { label: 'B', value: 'B', text: "I need to review what went wrong with my analysis" },
      { label: 'C', value: 'C', text: "I\'ll discuss this with my investment community" },
      { label: 'D', value: 'D', text: "Time to rebalance based on new market conditions" },
    ],
  },
  {
    id: 6,
    partTitle: "Part 2: Risk & Opportunity",
    text: "Which scenario sounds more appealing?",
    options: [
      { label: 'A', value: 'A', text: "30% chance of 100% returns, 70% chance of losing 20%" },
      { label: 'B', value: 'B', text: "80% chance of 15% returns, 20% chance of losing 5%" },
      { label: 'C', value: 'C', text: "Steady 8-10% returns with my investment group" },
      { label: 'D', value: 'D', text: "Returns that beat the market average consistently" },
    ],
  },
  {
    id: 16,
    partTitle: "Part 2: Risk & Opportunity",
    text: "How much volatility can you handle?",
    type: 'slider',
    options: [
      { label: "Zero Risk", value: 'A', text: "Completely Safe", description: "You want your balance to stay exactly where it is. No dips, no drama." },
      { label: "Low Risk", value: 'B', text: "Slow & Steady", description: "Small swings are okay, but big red days would make you lose sleep." },
      { label: "Moderate", value: 'C', text: "Calculated Growth", description: "You\'re fine with some volatility if it means better returns over time." },
      { label: "High Risk", value: 'D', text: "Growth Focused", description: "You understand that risk is part of the game and can handle big swings." },
      { label: "Max Risk", value: 'E', text: "Full Send Energy", description: "You\'re here for the moonshots. Volatility is just part of the vibe." },
    ],
  },
  {
    id: 7,
    partTitle: "Part 3: Info & Research",
    text: "Where do you get your investment ideas?",
    options: [
      { label: 'A', value: 'A', text: "Future trends, thought leaders, emerging technologies" },
      { label: 'B', value: 'B', text: "Financial statements, earnings reports, analyst research" },
      { label: 'C', value: 'C', text: "Recommendations from trusted investors or communities" },
      { label: 'D', value: 'D', text: "Market data, technical analysis, sector performance" },
    ],
  },
  {
    id: 8,
    partTitle: "Part 3: Info & Research",
    text: "You\'re reading about a company. What section interests you most?",
    options: [
      { label: 'A', value: 'A', text: "Vision, mission, and innovative products" },
      { label: 'B', value: 'B', text: "Balance sheet, profit margins, and debt levels" },
      { label: 'C', value: 'C', text: "Leadership team and company culture" },
      { label: 'D', value: 'D', text: "Market share, growth rate, and competitive position" },
    ],
  },
  {
    id: 9,
    partTitle: "Part 3: Info & Research",
    text: "How do you prefer to learn about investing?",
    options: [
      { label: 'A', value: 'A', text: "Big picture concepts, future scenarios, possibilities" },
      { label: 'B', value: 'B', text: "Step-by-step tutorials, case studies, detailed analysis" },
      { label: 'C', value: 'C', text: "Discussion groups, forums, learning from others" },
      { label: 'D', value: 'D', text: "Real-time data, charts, pattern recognition" },
    ],
  },
  {
    id: 10,
    partTitle: "Part 4: Management",
    text: "How often do you want to think about your investments?",
    options: [
      { label: 'A', value: 'A', text: "I\'m always thinking about future opportunities" },
      { label: 'B', value: 'B', text: "Regular scheduled reviews with deep analysis" },
      { label: 'C', value: 'C', text: "When my investment community discusses them" },
      { label: 'D', value: 'D', text: "When market conditions or data signals change" },
    ],
  },
  {
    id: 11,
    partTitle: "Part 4: Management",
    text: "What does a \"successful portfolio\" mean to you?",
    options: [
      { label: 'A', value: 'A', text: "Invested in companies changing the world" },
      { label: 'B', value: 'B', text: "Consistent returns with controlled risk" },
      { label: 'C', value: 'C', text: "Aligned with my values and community" },
      { label: 'D', value: 'D', text: "Outperforming benchmarks and peers" },
    ],
  },
  {
    id: 12,
    partTitle: "Part 4: Management",
    text: "You discover a fund that invests in disruptive but unproven technologies. You think:",
    options: [
      { label: 'A', value: 'A', text: "Perfect! This aligns with where the world is heading" },
      { label: 'B', value: 'B', text: "Too risky - I need more historical data" },
      { label: 'C', value: 'C', text: "Interesting if others I trust are investing too" },
      { label: 'D', value: 'D', text: "Let me see their track record vs. the market" },
    ],
  },
  {
    id: 13,
    partTitle: "Part 5: Values & Philosophy",
    text: "What matters most in the companies you invest in?",
    options: [
      { label: 'A', value: 'A', text: "Innovation and transformative potential" },
      { label: 'B', value: 'B', text: "Strong fundamentals and stability" },
      { label: 'C', value: 'C', text: "Ethical leadership and positive impact" },
      { label: 'D', value: 'D', text: "Market leadership and competitive advantage" },
    ],
  },
  {
    id: 14,
    partTitle: "Part 5: Values & Philosophy",
    text: "Your investment philosophy is best described as:",
    options: [
      { label: 'A', value: 'A', text: "\"Fortune favors the bold\" - big vision, big rewards" },
      { label: 'B', value: 'B', text: "\"Slow and steady wins the race\" - disciplined and methodical" },
      { label: 'C', value: 'C', text: "\"Together we\'re stronger\" - collaborative and community-driven" },
      { label: 'D', value: 'D', text: "\"Follow the data\" - evidence-based and adaptive" },
    ],
  },
  {
    id: 15,
    partTitle: "Part 5: Values & Philosophy",
    text: "Five years from now, you want to look back and feel:",
    options: [
      { label: 'A', value: 'A', text: "Proud I invested in companies that shaped the future" },
      { label: 'B', value: 'B', text: "Satisfied with consistent, predictable growth" },
      { label: 'C', value: 'C', text: "Connected to a community of like-minded investors" },
      { label: 'D', value: 'D', text: "Successful at beating market averages" },
    ],
  },
  {
    id: 17,
    partTitle: "Part 5: Values & Philosophy",
    text: "When do you need your money back?",
    type: 'slider',
    options: [
      { label: "Under 1yr", value: 'A', text: "Very Short Term", description: "You need liquidity soon and aren\`t looking for a long commitment." },
      { label: "1-3 Years", value: 'B', text: "Short Term", description: "You\'re building for a goal in the next few years." },
      { label: "3-7 Years", value: 'C', text: "Medium Term", description: "You\'re letting your money grow through market cycles." },
      { label: "7-15 Years", value: 'D', text: "Long Term", description: "You\'re focused on serious wealth building over a decade+." },
      { label: "15+ Years", value: 'E', text: "Legacy / End Game", description: "You\'re playing the infinite game for a legendary future." },
    ],
  },
];

export const INVESTOR_PROFILES: Record<string, InvestorProfile> = {
  VISIONARY: {
    id: 'VISIONARY',
    name: 'The Visionary Innovator',
    traits: ['Future-focused', 'Intuitive', 'Risk-tolerant'],
    description: "You\'re excited by disruption and innovation. You think long-term and focus on the big picture, believing that fortune favors the bold.",
    detailedAnalysis: [
      {
        title: "Your Strength: Foresight",
        content: "You have a natural ability to see where the world is heading. You don\`t just follow trends; you anticipate them, allowing you to get into transformative companies early."
      },
      {
        title: "The Visionary Trap",
        content: "Because you focus on the 'next big thing,' you might occasionally overlook current fundamentals. Balancing your disruptive bets with some stable assets is key to long-term survival."
      },
      {
        title: "Growth Strategy",
        content: "Focus on \`platform\` companies that aren\`t just building one product, but an entire ecosystem for the future (like AI infrastructure or space networks)."
      }
    ],
    investmentStyle: 'Growth/Innovation',
    riskTolerance: 'High',
    timeHorizon: '10+ years',
    portfolioAnalysis: "You indicated a strong preference for intuition and future-focused innovation. Because you\'re excited by companies \'ahead of their time\', we\'ve weighted your portfolio toward disruptive platforms.",
    allocation: [
      { 
        name: 'Disruptive Tech', 
        value: 45,
        explanation: "High-growth companies building technologies that will define the next decade.",
        examples: ["NVDA", "PLTR", "RKLB"]
      },
      { 
        name: 'Emerging Markets', 
        value: 20,
        explanation: "Investing in fast-growing economies outside of the US.",
        examples: ["EPI", "VWO", "MCHI"]
      },
      { 
        name: 'Biotech', 
        value: 15,
        explanation: "Cutting-edge medicine and gene-editing companies.",
        examples: ["CRSP", "VRTX", "IBB"]
      },
      { 
        name: 'Clean Energy', 
        value: 10,
        explanation: "Companies leading the transition to sustainable power.",
        examples: ["ICLN", "ENPH", "FSLR"]
      },
      { 
        name: 'Speculative', 
        value: 5,
        explanation: "Extremely high-risk, high-reward early stage bets.",
        examples: ["COIN", "HOOD", "BITO"]
      },
      { 
        name: 'Cash', 
        value: 5,
        explanation: "Dry powder to buy the dip when innovation hits a temporary bump.",
        examples: ["BIL", "SGOV"]
      },
    ],
    sectors: ['Artificial Intelligence', 'Space exploration', 'Gene editing', 'Next-gen computing', 'Revolutionary energy'],
    topFunds: [
      { 
        name: 'ARK Innovation ETF', 
        manager: 'Cathie Wood', 
        thesis: 'Disruptive innovation thesis focusing on AI, robotics, and genomic sequencing.', 
        matchReason: "Matches your high risk tolerance and focus on 5-10 year disruptive cycles.",
        risk: 'High' 
      },
      { 
        name: 'Baillie Gifford', 
        thesis: 'Long-term growth, seeking out the world\`s most transformative companies.', 
        matchReason: "Aligns with your 'big picture' thinking and willingness to wait for exponential rewards.",
        risk: 'High' 
      },
    ],
    sampleHoldings: ['TSLA', 'NVDA', 'COIN', 'SHOP', 'RKLB', 'CRSP', 'NET', 'PLTR'],
    radarData: [
      { subject: 'Innovation', A: 100, fullMark: 100 },
      { subject: 'Fundamentals', A: 30, fullMark: 100 },
      { subject: 'Community', A: 40, fullMark: 100 },
      { subject: 'Strategy', A: 60, fullMark: 100 },
    ],
  },
  ANALYTICAL: {
    id: 'ANALYTICAL',
    name: 'The Analytical Fundamentalist',
    traits: ['Data-driven', 'Methodical', 'Detail-oriented'],
    description: "You value proven track records and solid financials. You&ap os;re risk-aware and disciplined, following a slow and steady path to growth.",
    detailedAnalysis: [
      {
        title: "Your Strength: Precision",
        content: "You make decisions based on numbers, not hype. This discipline protects you from market bubbles and ensures your portfolio is built on a foundation of quality."
      },
      {
        title: "The Analytical Trap",
        content: "Over-analyzing can lead to \`paralysis by analysis.\` Sometimes a company\`s vision is more important than its current P/E ratio. Don\`t be afraid to occasionally trust growth potential."
      },
      {
        title: "Value Strategy",
        content: "Look for 'moats'—competitive advantages that allow a company to stay profitable for decades regardless of economic conditions."
      }
    ],
    investmentStyle: 'Value/Quality',
    riskTolerance: 'Low-Moderate',
    timeHorizon: '5-10 years',
    portfolioAnalysis: "Your methodical approach and focus on data-driven results suggested a low-to-moderate risk tolerance. We\`ve built your portfolio around proven fundamentals and established moats.",
    allocation: [
      { 
        name: 'Large-Cap Value', 
        value: 40,
        explanation: "Solid, established companies that are leaders in their industry.",
        examples: ["MSFT", "BRK.B", "AAPL"]
      },
      { 
        name: 'Dividend Aristocrats', 
        value: 25,
        explanation: "Companies that have increased their dividends every year for 25+ years.",
        examples: ["KO", "PG", "JNJ"]
      },
      { 
        name: 'Investment-Grade Bonds', 
        value: 15,
        explanation: "Low-risk loans to government or corporations that pay reliable interest.",
        examples: ["AGG", "BND", "LQD"]
      },
      { 
        name: 'International Blue Chips', 
        value: 10,
        explanation: "Top-tier established companies based outside of the US.",
        examples: ["ASML", "NSRGY", "SAP"]
      },
      { 
        name: 'REITs', 
        value: 5,
        explanation: "Real Estate Investment Trusts that pay out rent income as dividends.",
        examples: ["O", "AMT", "PLD"]
      },
      { 
        name: 'Cash', 
        value: 5,
        explanation: "Safe liquidity kept ready for when high-quality companies go on sale.",
        examples: ["BIL", "SGOV"]
      },
    ],
    sectors: ['Established tech', 'Healthcare leaders', 'Consumer staples', 'Financial services', 'Industrial stalwarts'],
    topFunds: [
      { 
        name: 'Berkshire Hathaway', 
        manager: 'Warren Buffett', 
        thesis: 'Value investing focused on companies with strong moats and durable cash flows.', 
        matchReason: "Perfectly aligns with your disciplined, data-first approach and focus on safety.",
        risk: 'Low-Moderate' 
      },
      { 
        name: 'Baupost Group', 
        manager: 'Seth Klarman', 
        thesis: 'Deep value analysis with a heavy focus on margin of safety and risk management.', 
        matchReason: "Matches your detail-oriented nature and preference for proven business models.",
        risk: 'Moderate' 
      },
    ],
    sampleHoldings: ['BRK.B', 'MSFT', 'JNJ', 'V', 'PG', 'JPM', 'WMT', 'KO'],
    radarData: [
      { subject: 'Innovation', A: 20, fullMark: 100 },
      { subject: 'Fundamentals', A: 100, fullMark: 100 },
      { subject: 'Community', A: 30, fullMark: 100 },
      { subject: 'Strategy', A: 50, fullMark: 100 },
    ],
  },
  COLLABORATIVE: {
    id: 'COLLABORATIVE',
    name: 'The Collaborative Networker',
    traits: ['Community-driven', 'Relationship-focused', 'Values-aligned'],
    description: "You value shared wisdom and ethical leadership. You seek alignment with your values and believe that we\`re stronger together.",
    detailedAnalysis: [
      {
        title: "Your Strength: Values",
        content: "You understand that companies with great culture and ethics often outperform in the long run. Your focus on community gives you a unique 'social edge' in your picks."
      },
      {
        title: "The Collaborative Trap",
        content: "Be careful of \`echo chambers.\` Just because a community loves a company doesn\`t mean it\`s a good investment. Always verify the financials alongside the sentiment."
      },
      {
        title: "Impact Strategy",
        content: "Focus on B-Corps and ESG leaders who are solving global problems. These companies often attract the best talent and most loyal customers."
      }
    ],
    investmentStyle: 'ESG/Impact/Social',
    riskTolerance: 'Moderate',
    timeHorizon: '5-15 years',
    portfolioAnalysis: "You value shared wisdom and community alignment over raw technical signals. Your portfolio is designed to reflect your commitment to ethical leadership and sustainable impact.",
    allocation: [
      { 
        name: 'ESG Leaders', 
        value: 35,
        explanation: "Companies with top scores in Environmental, Social, and Governance practices.",
        examples: ["NEE", "TSLA", "AAPL"]
      },
      { 
        name: 'Community-Backed', 
        value: 25,
        explanation: "Businesses with cult-like followings and strong internal culture.",
        examples: ["COST", "SBUX", "LULU"]
      },
      { 
        name: 'Impact Investing', 
        value: 15,
        explanation: "Directly funding solutions to social or environmental issues.",
        examples: ["STNE", "MPWR", "SEDG"]
      },
      { 
        name: 'Dividend Growth', 
        value: 15,
        explanation: "Shareholder-friendly companies that share profits consistently.",
        examples: ["TGT", "UNH", "V"]
      },
      { 
        name: 'Bonds', 
        value: 10,
        explanation: "Stable income through green bonds or social impact bonds.",
        examples: ["VGBX", "BNDX"]
      },
    ],
    sectors: ['ESG-compliant', 'Employee-owned', 'B-Corps', 'Community development', 'Sustainable brands'],
    topFunds: [
      { 
        name: 'Parnassus Investments', 
        thesis: 'ESG and values-based investing with a focus on high-quality companies and great culture.', 
        matchReason: "Aligns with your desire to invest in companies that treat people and the planet well.",
        risk: 'Moderate' 
      },
      { 
        name: 'Generation Investment', 
        manager: 'Al Gore', 
        thesis: 'Sustainable capitalism—long term investing integrated with sustainability research.', 
        matchReason: "Matches your philosophy that sustainable companies are better businesses.",
        risk: 'Moderate' 
      },
    ],
    sampleHoldings: ['AAPL', 'MSFT', 'NEE', 'UNH', 'TGT', 'COST', 'V', 'DIS'],
    radarData: [
      { subject: 'Innovation', A: 40, fullMark: 100 },
      { subject: 'Fundamentals', A: 50, fullMark: 100 },
      { subject: 'Community', A: 100, fullMark: 100 },
      { subject: 'Strategy', A: 30, fullMark: 100 },
    ],
  },
  STRATEGIC: {
    id: 'STRATEGIC',
    name: 'The Strategic Opportunist',
    traits: ['Market-aware', 'Tactical', 'Data-focused'],
    description: "You seek competitive advantages and respond to market signals. You follow the data and are adaptive to changing trends.",
    detailedAnalysis: [
      {
        title: "Your Strength: Agility",
        content: "You don\`t get emotionally attached to your picks. If the data changes, you move. This flexibility allows you to ride momentum and avoid sinking ships."
      },
      {
        title: "The Strategic Trap",
        content: "Over-trading can lead to high fees and taxes. Make sure your 'tactical moves' have enough conviction behind them to justify the turnover."
      },
      {
        title: "Momentum Strategy",
        content: "Use quantitative signals like Relative Strength or Moving Averages to confirm that a trend is real before jumping in."
      }
    ],
    investmentStyle: 'Momentum/Tactical',
    riskTolerance: 'Moderate-High',
    timeHorizon: '3-7 years',
    portfolioAnalysis: "Your focus on market momentum and real-time signals shows a tactical mindset. We\`ve optimized your allocation for agility, focusing on high-momentum sectors and technical breakouts.",
    allocation: [
      { 
        name: 'High-Momentum Growth', 
        value: 35,
        explanation: "Stocks that are currently showing the strongest price trends in the market.",
        examples: ["NVDA", "META", "AVGO"]
      },
      { 
        name: 'Sector Rotation', 
        value: 25,
        explanation: "Moving money into the sectors that are expected to perform best next.",
        examples: ["XLE", "XLF", "XLV"]
      },
      { 
        name: 'Emerging Leaders', 
        value: 15,
        explanation: "Mid-cap companies that are just starting to dominate their niche.",
        examples: ["NET", "ANET", "SNOW"]
      },
      { 
        name: 'International Growth', 
        value: 10,
        explanation: "Fast-growing companies in international tech hubs.",
        examples: ["ASML", "MELI", "TCEHY"]
      },
      { 
        name: 'Tactical Bonds', 
        value: 10,
        explanation: "Switching between different types of bonds based on interest rates.",
        examples: ["TLT", "HYG", "JNK"]
      },
      { 
        name: 'Alternatives', 
        value: 5,
        explanation: "Assets like commodities or gold used to hedge against specific risks.",
        examples: ["GLD", "SLV", "DBC"]
      },
    ],
    sectors: ['Market leaders', 'Cyclical opportunities', 'Emerging trends', 'Technical breakouts'],
    topFunds: [
      { 
        name: 'Renaissance Technologies', 
        thesis: 'Quantitative, data-driven strategies that exploit market inefficiencies.', 
        matchReason: "Matches your evidence-based, adaptive, and highly tactical mindset.",
        risk: 'High' 
      },
      { 
        name: 'Two Sigma', 
        thesis: 'Using machine learning and distributed computing to find alpha in global markets.', 
        matchReason: "Aligns with your preference for data-backed decisions over gut feelings.",
        risk: 'High' 
      },
    ],
    sampleHoldings: ['NVDA', 'META', 'GOOGL', 'AMD', 'AVGO', 'NOW', 'ANET', 'ASML'],
    radarData: [
      { subject: 'Innovation', A: 60, fullMark: 100 },
      { subject: 'Fundamentals', A: 40, fullMark: 100 },
      { subject: 'Community', A: 20, fullMark: 100 },
      { subject: 'Strategy', A: 100, fullMark: 100 },
    ],
  },
  BALANCED: {
    id: 'BALANCED',
    name: 'The Balanced Pragmatist',
    traits: ['Practical', 'Balanced', 'Diversified'],
    description: "You take a practical, all-weather approach to investing. You value both data and relationships, seeking consistent growth through diversification.",
    detailedAnalysis: [
      {
        title: "Your Strength: Stability",
        content: "You don\'t put all your eggs in one basket. By mixing different styles, you\'re prepared for any market condition, making you a very resilient investor."
      },
      {
        title: "The Balanced Trap",
        content: "Being 'too balanced' can sometimes mean missing out on massive growth in specific sectors. Consider keeping a small 'satellite' portion for bolder bets."
      },
      {
        title: "Pragmatic Strategy",
        content: "Focus on low-cost Index Funds for your core, and only pick individual stocks or active funds for sectors where you have high conviction."
      }
    ],
    investmentStyle: 'Core/Satellite Balanced',
    riskTolerance: 'Moderate',
    timeHorizon: '7-15 years',
    portfolioAnalysis: "You prefer a practical, diversified approach that values both data and relationships. This portfolio balances growth and stability to provide consistent returns across all market conditions.",
    allocation: [
      { 
        name: 'Core Index Funds', 
        value: 30,
        explanation: "The foundation of your portfolio—broad market exposure to the top 500 companies.",
        examples: ["VOO", "VTI", "SPY"]
      },
      { 
        name: 'Dividend Growth', 
        value: 20,
        explanation: "Stable companies that pay you to own them and grow their payouts over time.",
        examples: ["JNJ", "V", "MSFT"]
      },
      { 
        name: 'Quality Growth', 
        value: 20,
        explanation: "Tech and healthcare leaders that have strong growth but also solid profits.",
        examples: ["AAPL", "GOOGL", "UNH"]
      },
      { 
        name: 'International', 
        value: 15,
        explanation: "Exposure to global growth outside of North America.",
        examples: ["VEA", "VXUS", "VWO"]
      },
      { 
        name: 'Bonds', 
        value: 10,
        explanation: "Fixed-income assets that reduce the overall volatility of your portfolio.",
        examples: ["BND", "AGG", "VCIT"]
      },
      { 
        name: 'Thematic Satellites', 
        value: 5,
        explanation: "Small bets on specific trends you\'re personally interested in.",
        examples: ["BOTZ", "ICLN", "PAVE"]
      },
    ],
    sectors: ['Diversified across all sectors', 'Quality over speculation', 'Growth and income mix'],
    topFunds: [
      { 
        name: 'Vanguard S&P 500', 
        manager: 'John Bogle philosophy', 
        thesis: 'Low-cost indexing providing the most reliable long-term returns for most people.', 
        matchReason: "Perfectly matches your practical, no-nonsense approach to building wealth.",
        risk: 'Moderate' 
      },
      { 
        name: 'Fidelity Contrafund', 
        thesis: 'Investing in companies whose value is not fully recognized by the public.', 
        matchReason: "Aligns with your balanced approach—seeking growth while staying pragmatic about price.",
        risk: 'Moderate' 
      },
    ],
    sampleHoldings: ['VOO/SPY', 'VTI', 'AAPL', 'MSFT', 'JNJ', 'VZ', 'BND', 'VEA'],
    radarData: [
      { subject: 'Innovation', A: 50, fullMark: 100 },
      { subject: 'Fundamentals', A: 75, fullMark: 100 },
      { subject: 'Community', A: 75, fullMark: 100 },
      { subject: 'Strategy', A: 50, fullMark: 100 },
    ],
  },
  CONTRARIAN: {
    id: 'CONTRARIAN',
    name: 'The Contrarian Risk-Taker',
    traits: ['Bold', 'Analytical', 'Value-seeker'],
    description: "You\'re comfortable with volatility and seek undervalued opportunities where others see risk. You\'re bold yet analytical in your bets.",
    detailedAnalysis: [
      {
        title: "Your Strength: Courage",
        content: "You have the stomach to buy when others are selling. This 'contrarian' mindset allows you to pick up high-quality assets at massive discounts during market panics."
      },
      {
        title: "The Contrarian Trap",
        content: "A company that is 'cheap' can sometimes stay cheap forever. Make sure there is a clear catalyst for a turnaround before you bet the farm on a fallen angel."
      },
      {
        title: "Special Situations Strategy",
        content: "Look for spin-offs, restructurings, or temporary scandals that don\`t damage the long-term earning power of a company."
      }
    ],
    investmentStyle: 'Contrarian/Special Situations',
    riskTolerance: 'High',
    timeHorizon: '5-10 years',
    portfolioAnalysis: "Your bold, analytical nature allows you to see value where others see risk. We\`ve designed this allocation to capitalize on turnaround stories and misunderstood businesses.",
    allocation: [
      { 
        name: 'Contrarian Bets', 
        value: 35,
        explanation: "Investing in sectors that are currently unloved or hated by the market.",
        examples: ["XLE", "KRE", "XLU"]
      },
      { 
        name: 'Turnaround Stories', 
        value: 25,
        explanation: "Companies with new leadership trying to fix past mistakes.",
        examples: ["INTC", "F", "PYPL"]
      },
      { 
        name: 'Deep Value', 
        value: 15,
        explanation: "Buying companies for less than the value of their actual assets.",
        examples: ["VNO", "T", "VZ"]
      },
      { 
        name: 'Special Situations', 
        value: 10,
        explanation: "Mergers, acquisitions, or spin-offs that create unique value.",
        examples: ["GE", "KVUE", "SRAD"]
      },
      { 
        name: 'Emerging Markets', 
        value: 10,
        explanation: "Beaten-down international markets with high recovery potential.",
        examples: ["BABA", "FXI", "EWZ"]
      },
      { 
        name: 'Options/Hedges', 
        value: 5,
        explanation: "Using derivatives to protect your downside or bet on volatility.",
        examples: ["VIX", "SPXS", "SH"]
      },
    ],
    sectors: ['Out-of-favor sectors', 'Restructuring companies', 'Cyclical bottoms', 'Misunderstood businesses'],
    topFunds: [
      { 
        name: 'Oaktree Capital', 
        manager: 'Howard Marks', 
        thesis: 'Focus on distressed debt and buying assets when there is maximum pessimism.', 
        matchReason: "Matches your bold, analytical approach to finding value in chaos.",
        risk: 'High' 
      },
      { 
        name: 'Scion Asset Management', 
        manager: 'Michael Burry', 
        thesis: 'Heavily analytical contrarian bets based on deep macroeconomic research.', 
        matchReason: "Perfect for your 'Value-seeker' personality and willingness to go against the crowd.",
        risk: 'Very High' 
      },
    ],
    sampleHoldings: ['BAC', 'C', 'XOM', 'BABA', 'NIO', 'INTC', 'F', 'GM'],
    radarData: [
      { subject: 'Innovation', A: 80, fullMark: 100 },
      { subject: 'Fundamentals', A: 40, fullMark: 100 },
      { subject: 'Community', A: 30, fullMark: 100 },
      { subject: 'Strategy', A: 80, fullMark: 100 },
    ],
  },
};

export const calculateProfile = (answers: Record<number, 'A' | 'B' | 'C' | 'D' | 'E'>): InvestorProfile => {
  const counts = { A: 0, B: 0, C: 0, D: 0, E: 0 };
  Object.entries(answers).forEach(([id, val]) => {
    // Exclude slider questions from the personality type count
    if (parseInt(id) !== 16 && parseInt(id) !== 17) {
      counts[val as keyof typeof counts]++;
    }
  });

  // Check for dominant profile (>= 7 out of 15, which is ~47%)
  let baseProfile: InvestorProfile;

  if (counts.A >= 7) baseProfile = INVESTOR_PROFILES.VISIONARY;
  else if (counts.B >= 7) baseProfile = INVESTOR_PROFILES.ANALYTICAL;
  else if (counts.C >= 7) baseProfile = INVESTOR_PROFILES.COLLABORATIVE;
  else if (counts.D >= 7) baseProfile = INVESTOR_PROFILES.STRATEGIC;
  else {
    // Find top 2 answer types
    const sortedEntries = (Object.entries(counts) as [keyof typeof counts, number][])
      .sort((a, b) => b[1] - a[1]);
    
    const [firstLetter, firstCount] = sortedEntries[0];
    const [secondLetter, secondCount] = sortedEntries[1];

    // If top 2 are truly balanced (within 1 point AND both >= 5), it's a mixed profile
    const difference = firstCount - secondCount;
    const isMixedProfile = difference <= 1 && firstCount >= 5 && secondCount >= 5;
    
    if (isMixedProfile) {
      const mixKey = [firstLetter, secondLetter].sort().join('');
      
      // Map mixed profiles based on combination
      switch (mixKey) {
        case 'BC': // Analytical + Collaborative
          baseProfile = INVESTOR_PROFILES.BALANCED;
          break;
        case 'AD': // Visionary + Strategic
          baseProfile = INVESTOR_PROFILES.CONTRARIAN;
          break;
        case 'AC': // Visionary + Collaborative
          baseProfile = INVESTOR_PROFILES.BALANCED;
          break;
        case 'BD': // Analytical + Strategic
          baseProfile = INVESTOR_PROFILES.STRATEGIC;
          break;
        case 'CD': // Collaborative + Strategic
          baseProfile = INVESTOR_PROFILES.BALANCED;
          break;
        case 'AB': // Visionary + Analytical
          baseProfile = firstLetter === 'A' ? INVESTOR_PROFILES.VISIONARY : INVESTOR_PROFILES.ANALYTICAL;
          break;
        default:
          baseProfile = INVESTOR_PROFILES.BALANCED;
      }
    } else {
      // No mixed profile detected - return the profile of the highest count
      switch (firstLetter) {
        case 'A': baseProfile = INVESTOR_PROFILES.VISIONARY; break;
        case 'B': baseProfile = INVESTOR_PROFILES.ANALYTICAL; break;
        case 'C': baseProfile = INVESTOR_PROFILES.COLLABORATIVE; break;
        case 'D': baseProfile = INVESTOR_PROFILES.STRATEGIC; break;
        default: baseProfile = INVESTOR_PROFILES.BALANCED;
      }
    }
  }

  // Refine the profile based on specific risk/time questions
  const profile = { ...baseProfile };

  if (answers[16]) {
    const riskMap: Record<string, string> = {
      A: 'Very Low',
      B: 'Low',
      C: 'Moderate',
      D: 'High',
      E: 'Very High'
    };
    profile.riskTolerance = riskMap[answers[16]];
  }

  if (answers[17]) {
    const timeMap: Record<string, string> = {
      A: '< 1 year',
      B: '1-3 years',
      C: '3-7 years',
      D: '7-15 years',
      E: '15+ years'
    };
    profile.timeHorizon = timeMap[answers[17]];
  }

  return profile;
};

// Fallback sample data when AI quota is exhausted
export const SAMPLE_PORTFOLIO_DATA = {
  indexes: [{
    name: "Bloom AI Index (Sample)",
    tickers: [
      { ticker: "NVDA", percentage: 0.08, text: "High-growth AI infrastructure leader matching your visionary traits." },
      { ticker: "MSFT", percentage: 0.08, text: "Stable tech giant with diverse revenue streams fitting a balanced approach." },
      { ticker: "AAPL", percentage: 0.07, text: "Consumer staple with strong brand loyalty and consistent returns." },
      { ticker: "AMZN", percentage: 0.07, text: "E-commerce and cloud dominance for long-term growth potential." },
      { ticker: "GOOGL", percentage: 0.07, text: "Information moats and AI innovation aligning with analytical thinking." },
      { ticker: "META", percentage: 0.06, text: "Social connectivity and digital advertising scale." },
      { ticker: "TSLA", percentage: 0.06, text: "Disruptive innovation in energy and transport for high risk tolerance." },
      { ticker: "AVGO", percentage: 0.06, text: "Semiconductor infrastructure critical for the digital economy." },
      { ticker: "LLY",  percentage: 0.05, text: "Healthcare innovation leader with strong pipeline." },
      { ticker: "JPM",  percentage: 0.05, text: "Financial fortress providing stability and dividends." },
      { ticker: "V",    percentage: 0.05, text: "Payment network dominance with high margins." },
      { ticker: "WMT",  percentage: 0.05, text: "Defensive retail play for recession resistance." },
      { ticker: "JNJ",  percentage: 0.05, text: "Healthcare conglomerate offering safety and income." },
      { ticker: "PG",   percentage: 0.05, text: "Consumer staples giant for portfolio ballast." },
      { ticker: "MA",   percentage: 0.05, text: "Global transaction processing with secular tailwinds." },
      { ticker: "COST", percentage: 0.05, text: "Membership model retail with loyal customer base." },
      { ticker: "HD",   percentage: 0.05, text: "Home improvement leader tied to housing market." }
    ]
  }],
  isSampleData: true
};
